import { useEffect, useRef } from 'react';
import { usePropertyField } from '@vibesites/runtime/react';

// MarketApts embeds the fee guide in an iframe that auto-resizes its height via
// their hosted iframe-resizer script. The per-property AIM code selects which
// community's fees to show (the `?code=` query param).
const IFRAME_RESIZER_SRC = 'https://www.marketapts.com/js/iframeResizer.min.js';

// Fallback used when a property has not been assigned an AIM code yet.
const DEFAULT_AIM_CODE = '1080WMRK';

declare global {
  interface Window {
    iFrameResize?: (options: Record<string, unknown>, target: string | HTMLElement) => void;
  }
}

interface FeeGuideEmbedProps {
  /** Optional wrapper className around the iframe. */
  className?: string;
  /** Explicit AIM property code override. When empty, falls back to the
      property's `custom_aim_property_code` field, then the default code. */
  aimCode?: string;
}

/**
 * Shared MarketApts fee-guide iframe. Owns the AIM-code lookup + iframe-resizer
 * wiring in one place so both the inline FeeGuide section and the FeeGuideModal
 * popup render the same embed. Mounting this component loads/initializes the
 * resizer, so callers that show it conditionally (e.g. the modal) should only
 * mount it while visible.
 */
export function FeeGuideEmbed({ className, aimCode: aimCodeOverride }: FeeGuideEmbedProps) {
  // The AIM property code maps to the `code` query param in the MarketApts
  // embed URL. Prefer an explicit override, then per-property data, then the
  // shared default so the embed always resolves to some community.
  const fieldCode = usePropertyField('custom_aim_property_code') as string | null;
  const aimCode =
    (aimCodeOverride && aimCodeOverride.trim()) || fieldCode || DEFAULT_AIM_CODE;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Load / initialize the MarketApts iframe resizer once the iframe is mounted.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const applyResize = () => {
      if (window.iFrameResize && iframeRef.current) {
        window.iFrameResize({ log: false }, iframeRef.current);
      }
    };

    // Script already loaded (e.g. another instance on the page) — just init.
    if (window.iFrameResize) {
      applyResize();
      return;
    }

    // Otherwise load the MarketApts resizer once, then initialize on load.
    let script = document.querySelector<HTMLScriptElement>(`script[src="${IFRAME_RESIZER_SRC}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = IFRAME_RESIZER_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
    script.addEventListener('load', applyResize);
    return () => {
      script?.removeEventListener('load', applyResize);
    };
  }, [aimCode]);

  const iframeSrc = `https://www.marketapts.com/iframes/fee-guide?code=${encodeURIComponent(aimCode)}`;

  return (
    <div className={className}>
      <iframe
        ref={iframeRef}
        id="fee-guide"
        title="Fee Guide"
        src={iframeSrc}
        style={{ border: 'none', width: '1px', minWidth: '100%' }}
      />
    </div>
  );
}

export default FeeGuideEmbed;
